import { create } from 'zustand';
import type { AnsweredTrial, Choice, Trial } from '../lib/types';
import { buildFingerprint, createTrials, estimateSubjectiveValue, updateTrialBounds } from '../lib/discounting';

export type GameStore = {
  seed: string;
  trials: Trial[];
  answers: AnsweredTrial[];
  currentIndex: number;
  currentStartedAt: number;
  startedAtIso: string;
  reset: () => void;
  begin: () => void;
  answer: (choice: Choice) => void;
  isComplete: () => boolean;
};

function freshSeed() {
  return `patience-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function freshState(seed = freshSeed()) {
  return {
    seed,
    trials: createTrials(seed),
    answers: [] as AnsweredTrial[],
    currentIndex: 0,
    currentStartedAt: performance.now(),
    startedAtIso: new Date().toISOString()
  };
}

export const useGameStore = create<GameStore>((set, get) => ({
  ...freshState(),
  reset: () => set(freshState()),
  begin: () => set((state) => ({ ...state, currentStartedAt: performance.now(), startedAtIso: new Date().toISOString() })),
  answer: (choice) => {
    const state = get();
    const trial = state.trials[state.currentIndex];
    if (!trial) return;
    const reactionMs = Math.max(0, Math.round(performance.now() - state.currentStartedAt));
    const subjectiveEstimate = estimateSubjectiveValue(trial.low, trial.high, choice, trial.soonerAmount);
    const answered: AnsweredTrial = {
      ...trial,
      trialIndex: state.answers.length,
      choice,
      reactionMs,
      subjectiveEstimate,
      answeredAt: new Date().toISOString()
    };
    const nextAnswers = [...state.answers, answered];
    const nextTrials = updateTrialBounds(state.trials, state.answers, trial, choice);
    set({
      answers: nextAnswers,
      trials: nextTrials,
      currentIndex: state.currentIndex + 1,
      currentStartedAt: performance.now()
    });
  },
  isComplete: () => get().currentIndex >= get().trials.length
}));

export const selectFingerprint = (state: GameStore) => buildFingerprint(state.answers);
